import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppDispatch } from '../../redux/hooks';
import { setSelectedLeague } from '../../redux/slice/selectedLeagueSlice';
import { setActiveTab } from '../../redux/slice/activeTabSlice';
import { Images } from '../../utils/imageConstants';
import {
  fetchAllLeagues,
  mapLeagueItemToGetAllLeagues,
  bulkUploadGround,
  createLeague,
} from './api/myLeaguesApi';
import type { ICreateLeagueRequest } from './types/myLeagues';
import { useQueryClient } from '@tanstack/react-query';
import LeagueDetailsStep, { type LeagueDetailsValues } from './components/steps/LeagueDetailsStep';
import GroundsStep, { buildGroundBulkItems, type GroundRow } from './components/steps/GroundsStep';
import LeaguePreviewStep from './components/steps/LeaguePreviewStep';
import { showCreateLeagueSuccess } from '../../utils/Confirmalert';
import './CreateLeague.scss';

const STEPS = [
  { id: 1, label: 'League Details' },
  { id: 2, label: 'Ground Details' },
  { id: 3, label: 'Review' },
] as const;

const TrophyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const GroundIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 21V8l9-5 9 5v13" />
    <path d="M9 21v-6h6v6" />
    <path d="M3 12h18" />
  </svg>
);

const PreviewIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const STEP_ICONS: Record<number, React.FC> = {
  1: TrophyIcon,
  2: GroundIcon,
  3: PreviewIcon,
};

function CreateLeague() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [leagueDetails, setLeagueDetails] = useState<LeagueDetailsValues | null>(null);
  const [grounds, setGrounds] = useState<GroundRow[]>([]);
  const [createdLeagueId, setCreatedLeagueId] = useState<string | null>(null);
  const [groundsUploaded, setGroundsUploaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const leagueName = leagueDetails?.leagueName ?? '';

  const handleStep1Next = useCallback((data: LeagueDetailsValues) => {
    setLeagueDetails(data);
    setCompletedSteps((prev) => new Set([...prev, 1]));
    setCurrentStep(2);
  }, []);

  const handleStep2Next = useCallback((rows: GroundRow[]) => {
    setGrounds(rows);
    setGroundsUploaded(false);
    setCompletedSteps((prev) => new Set([...prev, 2]));
    setCurrentStep(3);
  }, []);

  const handleStep2Skip = useCallback(() => {
    setGrounds([]);
    setGroundsUploaded(false);
    setCompletedSteps((prev) => new Set([...prev, 2]));
    setCurrentStep(3);
  }, []);

  const finalizeAndShowSuccess = useCallback((leagueId: string) => {
    setCompletedSteps((prev) => new Set([...prev, 3]));
    showCreateLeagueSuccess({
      onProceed: async () => {
        const leagues = await queryClient.fetchQuery({
          queryKey: ['myLeagues', 'allLeague'],
          queryFn: fetchAllLeagues,
        });
        const league = (leagues as unknown as { leagueId?: string;[k: string]: unknown }[])?.find?.(
          (l: { leagueId?: string }) => l.leagueId === leagueId
        );
        if (league) {
          dispatch(setSelectedLeague(mapLeagueItemToGetAllLeagues(league as never)));
        }
        dispatch(setActiveTab('leagues-home'));
        sessionStorage.setItem('activeTab', 'leagues-home');
        navigate('/leagues-home');
      },
    });
  }, [queryClient, dispatch, navigate]);

  const handleCreate = useCallback(async () => {
    if (!leagueDetails) {
      toast.error('League details are missing.');
      return;
    }
    if (!leagueDetails.logoFile || !leagueDetails.bannerFile) {
      toast.error('League logo and banner are required.');
      return;
    }
    setSubmitting(true);
    try {
      let leagueId = createdLeagueId;
      if (!leagueId) {
        const payload: ICreateLeagueRequest = {
          leagueName: leagueDetails.leagueName,
          organizerName: leagueDetails.organizerName,
          organizerPhoneNumber:
            leagueDetails.organizerPhoneNumber.replace(/\D/g, '').slice(-10) ||
            leagueDetails.organizerPhoneNumber,
          city: leagueDetails.city,
          state: leagueDetails.state,
          country: leagueDetails.country,
          countryCode: parseInt(leagueDetails.countryCode, 10) || 91,
          allowPlayersToJoin: true,
        };
        if (leagueDetails.organizerEmailId) payload.organizerEmailId = leagueDetails.organizerEmailId;
        if (leagueDetails.association) payload.association = leagueDetails.association;
        leagueId = await createLeague(payload, leagueDetails.logoFile, leagueDetails.bannerFile);
        setCreatedLeagueId(leagueId);
      }

      const items = buildGroundBulkItems(grounds);
      if (items.length > 0 && !groundsUploaded) {
        await bulkUploadGround(leagueId, { grounds: items });
        setGroundsUploaded(true);
      }
      finalizeAndShowSuccess(leagueId);
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { message?: string; error?: string } | string } })?.response;
      const data = res?.data;
      const msg =
        typeof data === 'string'
          ? data
          : (data?.message ?? data?.error ?? null);
      if (msg?.toLowerCase().includes('guest cannot create')) {
        toast.error('Please sign in to create a league.');
        navigate('/login');
        return;
      }
      const status = res?.status;
      const isSuccess = typeof status === 'number' && status >= 200 && status < 300;
      toast.error(msg || (isSuccess ? 'Failed to save league.' : 'Something went wrong'));
    } finally {
      setSubmitting(false);
    }
  }, [leagueDetails, grounds, groundsUploaded, createdLeagueId, finalizeAndShowSuccess, navigate]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
    else navigate('/my-leagues');
  }, [currentStep, navigate]);

  return (
    <div className="create-tournament-wrapper create-league-page">
      <div className="background-image-wrapper">
        <div className="background-image">
          <p>{leagueName || 'Create League'}</p>
        </div>
      </div>
      <div className="create-tourmament-wrap">
        <div className="ct-header">
          <img
            src={Images.previous_icon}
            alt="Back"
            onClick={handleBack}
            style={{ cursor: 'pointer' }}
          />
          <h1 className="heading-league">Create League</h1>
        </div>

        <div className="create-league-stepper-wrap">
          <ol className="create-league-stepper" role="list">
            {STEPS.map((step, index) => {
              const Icon = STEP_ICONS[step.id];
              const isActive = currentStep === step.id;
              const isCompleted = completedSteps.has(step.id);
              const isReached = isActive || isCompleted;
              return (
                <li
                  key={step.id}
                  className={`create-league-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                >
                  {index > 0 && (
                    <span
                      className={`create-league-step-connector ${currentStep >= step.id || completedSteps.has(step.id - 1) ? 'dark' : ''}`}
                      aria-hidden
                    />
                  )}
                  <span className={`create-league-step-dot ${isReached ? 'reached' : ''}`}>
                    <span className="create-league-step-dot-inner">
                      <Icon />
                    </span>
                  </span>
                  <span className="create-league-step-label">{step.label}</span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="create-league-content">
          {currentStep === 1 && (
            <LeagueDetailsStep
              initialValues={leagueDetails ?? undefined}
              onNext={handleStep1Next}
            />
          )}
          {currentStep === 2 && (
            <GroundsStep
              leagueId={createdLeagueId}
              initialRows={grounds.length > 0 ? grounds : undefined}
              onNext={handleStep2Next}
              onSkip={handleStep2Skip}
              onBack={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 3 && leagueDetails && (
            <LeaguePreviewStep
              data={leagueDetails}
              grounds={grounds}
              onPrevious={() => setCurrentStep(2)}
              onCreate={handleCreate}
              isSubmitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateLeague;
